import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { defineCourseBuilderForm } from './CourseBuilderForm';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useBloc } from '@core/utils/bloc';
import { CourseBuilderPageBloc } from './CourseBuilderPageBloc';
import { CourseBuilderContext } from './CourseBuilderContext';
import { useLazyEffect } from '@core/utils/react';
import { useMainPageConfig, HeaderVisibility } from '@modules/dashboard/DashboardPage/MainPageContext';
import { CourseBuilderBlocs, CourseBuilderBlocsHandle } from './Components/CourseBuilderPage';
import { CourseBuilderSections } from './Components/SectionBar';
import { CourseBuilderHeader } from './Components/CourseBuilderHeader';
import { toMoment } from '@core/utils/time';
import { useHistory, useRouteParam } from '@core/router';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { CourseBuilder } from '../Blocks/types';
import { GoogleFontLoader } from '@modules/common/GoogleFontLoader';
import { css } from '@emotion/react';
import { TextBlockVariation } from '../Blocks/TextBlock';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import OpenAI from 'openai';
import './index.scss';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { CommonService } from '@business/services';
import { ImageFinderAPI } from '@business/api/image_finder';
import { Alert } from '@core/components/Alert';

const AI_BLOCK_ID_PREFIX = 'ai-widget-';

const isAIBlock = (block: { id?: string }) => !!block?.id && block.id.startsWith(AI_BLOCK_ID_PREFIX);

function toImageQuery(...candidates: (string | undefined)[]) {
  const text = candidates.find(c => !!R.trim(c || '')) || '';
  return R.trim(text.replace(/<[^>]*>/g, ''))
    .split(/\s+/)
    .slice(0, 6)
    .join(' ');
}

export const CourseBuilderPage = memo(() => {
  const lessonId = useRouteParam('id');

  const history = useHistory();
  const returnUrl: string | null = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const value = params.get('returnUrl');
      return value ? decodeURIComponent(value) : null;
    } catch (error) {
      console.warn('Failed to parse returnUrl from query string:', error);
      return null;
    }
  }, []);

  const bloc = useBloc(CourseBuilderPageBloc, lessonId);
  const lesson = useObservable(bloc.lesson$);
  const course = useObservable(bloc.course$);
  const loading = useLoadingState(bloc.loading);
  const form = useMemo(() => defineCourseBuilderForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const currentSection = useNonNilObservable(bloc.currentSection$);
  const [sidebar, setSidebar] = useState(true);
  const blockRefs = useMemo(() => R.times(formValue.blocks.length).map(() => React.createRef<HTMLDivElement>()), [formValue.blocks.length]);
  const [moveTo, setMoveto] = React.useState<number | null>(null);
  const [lastSaved, setLastSaved] = useState<number | null>(null);
  const [canSave, setCanSave] = useState(false);
  const [isInit, setInit] = useState(false);
  const settings = useSettings();
  const applyCourseChanges = useMemo(() => CommonService.applyCourseChanges(settings), [settings]);
  const blockHandlesRef = useRef<CourseBuilderBlocsHandle>(null);

  useEffect(() => {
    if (formValue) {
      setCanSave(true);
    }
  }, [formValue]);

  useLazyEffect(() => {
    if (moveTo != null && blockRefs[moveTo] && blockRefs[moveTo].current) {
      window.scrollTo({ top: blockRefs[moveTo].current!.offsetTop, behavior: 'smooth' });
      setMoveto(null);
    }
  }, [moveTo, blockRefs]);

  const toggleSidebar = useCallback(() => setSidebar(s => !s), []);

  useMainPageConfig(() => ({ footer: false, toolbar: false, header: HeaderVisibility.auto }));

  useEffect(() => {
    const handlePopState = () => {
      if (!lessonId || typeof window === 'undefined') return;

      const isCourseBuilder = window.location.pathname.includes(`/course-builder/${lessonId}`);
      if (!isCourseBuilder && returnUrl) {
        try {
          if (returnUrl.startsWith('/')) {
            history.push(returnUrl);
          } else {
            console.warn('Blocked unsafe returnUrl:', returnUrl);
          }
        } catch (err) {
          console.error('Error redirecting to returnUrl:', err);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [lessonId, returnUrl, history]);

  const onSave = useCallback(() => {
    if (loading.loaded) {
      setLastSaved(toMoment().unix());

      bloc.updateFormData(lessonId, JSON.stringify(formValue)).subscribe();
      setCanSave(false);
    }
  }, [bloc, lessonId, formValue, loading.loaded]);

  useLazyEffect(
    () => {
      //   if (applyCourseChanges) return;
      if (formValue) onSave();
    },
    [formValue, onSave],
    5000,
  );

  useEffect(() => {
    if (loading.loaded && lesson && !isInit) {
      setInit(true);

      const data = lesson.data;
      if (data && data.blocks) {
        form.patchValue(data);
      } else {
        form.patchValue({
          title: lesson.name,
          blocks: [
            {
              id: 'section-title-0',
              type: CourseBuilder.BlockType.text,
              data: { title: 'Section Title' },
              variation: TextBlockVariation.paragraphWithHeading,
              menuOrder: 0,
              isSection: true,
              title: undefined,
            },
          ],
        });
      }
    }
  }, [loading, isInit, form, lesson]);

  const getCourseDetails = useCallback(
    (callId: string, args: any) => {
      const courseData = {
        courseId: course.id,
        sections: course.sections.map(section => ({
          id: section.id,
          name: section.name,
          steps: section.steps.map(step => ({
            id: step.id,
            name: step.name,
            data: step.data,
            settings: step.settings,
          })),
        })),
      };
      return of({ tool_call_id: callId, output: JSON.stringify(courseData) });
    },
    [course],
  );

  const tools: OpenAI.Responses.Tool[] = useMemo(() => {
    return [
      {
        type: 'function',
        name: 'getCourseDetails',
        description: 'Fetches the current courseId and existing section/step structure. Must be used before any other action.',
        strict: false,
        parameters: {},
      },
    ];
  }, []);

  const onToolCall = useCallback(
    (callId: string, fnName: string, args: any) => {
      const functionMap: { [key: string]: (callId: string, args: any) => Observable<any> } = {
        getCourseDetails,
      };
      const argsT = JSON.parse(args);
      if (functionMap[fnName]) {
        return functionMap[fnName](callId, argsT) as any;
      } else {
        console.error(`Function ${fnName} not found`);
      }
      return of(null);
    },
    [getCourseDetails],
  );

  const fetchStockImages = useCallback(
    (blocks: any[], fallbackQuery: string) => {
      const requests: { blockId: string; itemIndex: number | null; query: string }[] = [];

      blocks.forEach(block => {
        if (block.type === 'image') {
          requests.push({
            blockId: block.id,
            itemIndex: null,
            query: toImageQuery(block.data?.imageQuery, block.data?.text, block.title, fallbackQuery),
          });
        } else if (block.type === 'gallery' && Array.isArray(block.data?.items)) {
          block.data.items.forEach((item: any, index: number) => {
            requests.push({ blockId: block.id, itemIndex: index, query: toImageQuery(item?.text, block.title, fallbackQuery) });
          });
        }
      });

      if (!requests.length) return;

      const queries = R.uniq(requests.map(r => r.query));
      forkJoin(
        queries.map(query =>
          ImageFinderAPI.fetchImages({ search: query, source: 'unsplash', page: 1 }).pipe(
            map(response => ({ query, images: response.images })),
            catchError(() => of({ query, images: [] })),
          ),
        ),
      ).subscribe(results => {
        const imagesByQuery = new Map(results.map(r => [r.query, r.images]));
        const usedByQuery = new Map<string, number>();

        const nextImageFor = (query: string) => {
          const images = imagesByQuery.get(query) || [];
          if (!images.length) return null;
          const used = usedByQuery.get(query) || 0;
          usedByQuery.set(query, used + 1);
          return images[used % images.length].downloadUrl;
        };

        const updatedBlocks = (form.value.blocks || []).map(block => {
          const blockRequests = requests.filter(r => r.blockId === block.id);
          if (!blockRequests.length) return block;

          if (block.type === 'image') {
            const image = nextImageFor(blockRequests[0].query);
            return image ? { ...block, data: { ...block.data, image } } : block;
          }

          const items = (block.data?.items || []).map((item: any, index: number) => {
            const request = blockRequests.find(r => r.itemIndex === index);
            const image = request ? nextImageFor(request.query) : null;
            return image ? { ...item, image } : item;
          });
          return { ...block, data: { ...block.data, items } };
        });

        form.patchValue({ blocks: updatedBlocks });
        setCanSave(true);
      });
    },
    [form],
  );

  const applyAIBlocks = useCallback(
    (result: any, replacePrevious: boolean) => {
      const sortedBlocks = R.sortBy(form.value.blocks || [], b => b?.menuOrder || 0);
      const keptBlocks = replacePrevious ? sortedBlocks.filter(b => !isAIBlock(b)) : sortedBlocks;

      // when replacing, insert the new content where the previous AI content started
      let insertAt = keptBlocks.length;
      if (replacePrevious) {
        const firstAIIndex = sortedBlocks.findIndex(isAIBlock);
        if (firstAIIndex >= 0) insertAt = sortedBlocks.slice(0, firstAIIndex).filter(b => !isAIBlock(b)).length;
      }

      const newBlocks = result.blocks.map((block: any) => {
        const type = block.type === 'drill' ? block.variation : block.type;
        return {
          ...block,
          type,
          id: `${AI_BLOCK_ID_PREFIX}${type}-${block.variation}` + R.random(1, 9999),
          isSection: false,
        };
      });

      const blocks = [...keptBlocks.slice(0, insertAt), ...newBlocks, ...keptBlocks.slice(insertAt)].map((block, index) => ({
        ...block,
        menuOrder: index,
      }));

      form.patchValue({ blocks });
      setCanSave(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchStockImages(newBlocks, result.title || form.value.title);
    },
    [form, fetchStockImages],
  );

  const onAIOutput = useCallback(
    (output: string) => {
      try {
        let result = output as any;
        while (typeof result === 'string') {
          result = JSON.parse(result);
        }

        if (typeof result === 'object' && result.blocks && Array.isArray(result.blocks)) {
          const hasPreviousAIBlocks = (form.value.blocks || []).some(isAIBlock);
          if (hasPreviousAIBlocks) {
            Alert.info({
              title: 'Update AI Content',
              content: 'This step already has AI-generated content. Replace it with this new version, or keep it and add the new content below?',
              confirmText: 'Replace previous AI content',
              cancelText: 'Add below',
              onConfirm: () => applyAIBlocks(result, true),
              onCancel: () => applyAIBlocks(result, false),
            });
          } else {
            applyAIBlocks(result, false);
          }
        } else {
          throw new Error('Parsed output is not a valid step object.');
        }
      } catch (e) {
        console.error('Error parsing AI output:', e);
      }
    },
    [form, applyAIBlocks],
  );

  if (loading.loading) return <TopLoadingBar />;

  return (
    <div id="course-builder" style={{ margin: 0 }}>
      <CourseBuilderContext.Provider value={{ form, currentSection, bloc, sidebar, toggleSidebar, lessonId }}>
        <CourseBuilderHeader
          id={lessonId}
          lastSaved={lastSaved}
          onSave={onSave}
          canSave={canSave}
          onAIOutput={onAIOutput}
          tools={tools}
          onToolCall={onToolCall}
          courseId={lesson?.courseId}
          lesson={lesson}
        />
        <CourseBuilderSections moveTo={setMoveto} handleRef={blockHandlesRef} />
        {!!formValue.font && <GoogleFontLoader font={formValue.font} />}
        <div
          css={css(`
          h1 {
            font-size: ${formValue.headingFontSize + 'px'};
          }
        `)}
          id="course-builder-container"
          style={{
            marginLeft: sidebar ? 250 : 0,
            fontSize: formValue.fontSize + 'px',
            fontFamily: formValue.font?.family,
            fontWeight: formValue.fontWeight,
          }}
        >
          <CourseBuilderBlocs handlesRef={blockHandlesRef} blockRefs={blockRefs} moveTo={setMoveto} lessonId={lessonId} />
        </div>
      </CourseBuilderContext.Provider>
    </div>
  );
});
